(() => {
  class TextHighlightSection extends HTMLElement {
    constructor() {
      super();
      this.words = [];
      this.scrollElement = null;
      this.observer = null;
      this.rafId = null;
      this.boundScrollHandler = null;
      this.boundResizeHandler = null;
      this.savedPosition = 0;
      this.highlightedWords = 0;
      this.isActive = false;
    }

    connectedCallback() {
      this.scrollElement = this.querySelector('[data-scroll-element]');
      this.words = Array.from(this.querySelectorAll('.text-highlight__word'));

      if (this.words.length === 0) return;

      this.boundScrollHandler = this.onScroll.bind(this);
      this.boundResizeHandler = this.onResize.bind(this);
      this.setupObserver();
      this.updateHighlight();
    }

    disconnectedCallback() {
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }
      if (this.rafId) {
        cancelAnimationFrame(this.rafId);
        this.rafId = null;
      }
      window.removeEventListener('scroll', this.boundScrollHandler);
      window.removeEventListener('resize', this.boundResizeHandler);
    }

    setupObserver() {
      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              window.addEventListener('scroll', this.boundScrollHandler, { passive: true });
              window.addEventListener('resize', this.boundResizeHandler, { passive: true });
              this.updateHighlight();
            } else {
              window.removeEventListener('scroll', this.boundScrollHandler);
              window.removeEventListener('resize', this.boundResizeHandler);
            }
          });
        },
        { threshold: 0 }
      );

      this.observer.observe(this.scrollElement || this);
    }

    onScroll() {
      if (this.rafId) cancelAnimationFrame(this.rafId);
      this.rafId = requestAnimationFrame(() => {
        this.updateHighlight();
      });
    }

    onResize() {
      this.savedPosition = 0;
      this.isActive = false;
      this.onScroll();
    }

    updateHighlight() {
      const element = this.scrollElement || this;
      const rect = element.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const elementHeight = rect.height;

      if (rect.top < viewportHeight && rect.bottom > 0) {
        if (!this.isActive) {
          this.savedPosition = scrollTop - (viewportHeight - rect.top);
          this.isActive = true;
        }

        const delay = viewportHeight * 0.35;
        const scrolledPast = Math.max(0, scrollTop - this.savedPosition - delay);
        const scrollRange = elementHeight;
        const progress = Math.max(0, Math.min(1, scrolledPast / scrollRange));
        const targetWords = Math.floor(progress * this.words.length);

        if (targetWords !== this.highlightedWords) {
          this.highlightedWords = targetWords;
          this.words.forEach((word, index) => {
            if (index < this.highlightedWords) {
              word.classList.add('text-highlight__word--active');
            } else {
              word.classList.remove('text-highlight__word--active');
            }
          });
        }
      } else if (rect.top >= viewportHeight) {
        if (this.isActive) {
          this.savedPosition = 0;
          this.highlightedWords = 0;
          this.isActive = false;
          this.words.forEach((word) => {
            word.classList.remove('text-highlight__word--active');
          });
        }
      }
    }
  }

  if (!customElements.get('text-highlight-section')) {
    customElements.define('text-highlight-section', TextHighlightSection);
  }
})();